import { ReactElement, useEffect, useState, ChangeEvent, useMemo } from 'react';
import {
  Plan,
  Product,
  Discount,
  PlanFamily,
  ProductType,
  OrderContext,
  AcquisitionChannel,
  BillingChannel,
} from '@localstack/types';
import { curry, omit } from 'lodash';
import moment from 'moment/moment';
import {
  Box,
  Button,
  Checkbox,
  Grid,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Typography,
  TextField,
  FormControl,
  Select,
  InputLabel,
  FormHelperText,
  Divider,
} from '@mui/material';
import { PRICELESS_PLAN_FAMILIES } from '@localstack/constants';

import { formatBytes, formatMonetaryAmount, getProductEstimations, getProductVolume } from '@localstack/services';

import { PlanProductIcon } from '../../../../display';
import { computeConstructorHints } from '../../../../utils';

import { HintsAlert } from './Hints';

const SELF_SERVE_DEFAULT_PLAN_FAMILIES = [
  PlanFamily.FREEMIUM_PLANS,
  PlanFamily.STUDENT,
  PlanFamily.TRIAL_PLANS,
  PlanFamily.HOBBY_PLANS,
];

export interface ConstructorFormProps {
  edit?: boolean;
  plan: Plan;
  products: Product[];
  selectedProducts: Product[];
  discounts: Discount[];
  selectedDiscount?: Optional<Discount>;
  context: OrderContext;
  onUpdateContext?: (context: Partial<OrderContext>) => unknown;
  onUpdateDiscount?: (discount: Optional<Discount>) => unknown;
  onUpdateProducts?: (products: Product[]) => unknown;
}

export const ConstructorForm = ({
  edit,
  plan,
  products,
  selectedProducts,
  discounts,
  selectedDiscount,
  context,
  onUpdateContext,
  onUpdateDiscount,
  onUpdateProducts,
}: ConstructorFormProps): ReactElement => {
  // --- helpers & derived flags ------------------------------------------------
  const isEditing = !!edit;

  const [manualChannelOverride, setManualChannelOverride] = useState({ acquisition: false, billing: false });

  const isSelfServeDefaultPlan = SELF_SERVE_DEFAULT_PLAN_FAMILIES.includes(plan.family);
  const defaultAcquisition = isSelfServeDefaultPlan ? AcquisitionChannel.SELF_SERVE : AcquisitionChannel.SALES;
  const defaultBilling = isSelfServeDefaultPlan ? BillingChannel.NONE : BillingChannel.INVOICE;

  const acquisition: AcquisitionChannel = (context.acquisition_channel as AcquisitionChannel) ?? defaultAcquisition;
  const billing: BillingChannel = (context.billing_channel as BillingChannel) ?? defaultBilling;

  const isSelfServe = acquisition === AcquisitionChannel.SELF_SERVE;
  const isSalesLed = acquisition === AcquisitionChannel.SALES;
  const isUpdateSelfServe = isEditing && isSelfServe;
  const isUpdateSalesLed = isEditing && isSalesLed;

  const isStripeBilling = billing === BillingChannel.STRIPE;

  const displayDiscounts = useMemo(() => {
    const list = discounts ?? [];
    return selectedDiscount && !list.some((d) => d.id === selectedDiscount.id) ? [...list, selectedDiscount] : list;
  }, [discounts, selectedDiscount?.id]);

  const hasSeatsProduct = selectedProducts.some((p) => p.type === ProductType.SEATS);

  const { info: infoHints, warn: warnHints } = computeConstructorHints({
    isSelfServe,
    isStripeBilling,
    isEditing,
    isSalesLed,
    planFamily: plan.family,
    planIntervalMonths: plan.interval_months,
    hasSeatsProduct,
    selectedDiscount,
    context: {
      interval: context.interval,
      volumes: context.volumes,
      proration_behavior: context.proration_behavior,
      custom_renewal_date: context.custom_renewal_date,
    },
  });

  // --- generic context change handler ----------------------------------------
  const handleContextChange = curry(
    (attribute: keyof OrderContext, type: 'number' | 'string' | 'timestamp', e: ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;

      if (type === 'number') {
        if (/[0-9]+/.test(value ?? '')) {
          onUpdateContext?.({ [attribute]: Number.parseInt(value, 10) });
        } else {
          onUpdateContext?.({ [attribute]: undefined });
        }
        return;
      }

      if (type === 'string') {
        return onUpdateContext?.({ [attribute]: value ?? '' });
      }

      if (type === 'timestamp') {
        return onUpdateContext?.({ [attribute]: value ? moment(value).unix() : undefined });
      }

      throw new Error(`unknown type ${type}`);
    },
  );

  // --- defaults for CREATE ----------------------------------------------------
  // Re-applies the plan-driven default whenever the plan family changes, unless the user manually picked a channel.
  useEffect(() => {
    if (isEditing) return;

    const update: Partial<OrderContext> = {};
    if (!manualChannelOverride.acquisition && context.acquisition_channel !== defaultAcquisition) {
      update.acquisition_channel = defaultAcquisition;
    }
    if (!manualChannelOverride.billing && context.billing_channel !== defaultBilling) {
      update.billing_channel = defaultBilling;
    }
    if (Object.keys(update).length > 0) {
      onUpdateContext?.(update);
    }
  }, [isEditing, defaultAcquisition, defaultBilling, manualChannelOverride.acquisition, manualChannelOverride.billing]);

  // Reset custom volumes selection on plan change & keep interval dynamic to plan
  useEffect(() => {
    // reset custom volumes selection on
    onUpdateContext?.({ volumes: {} });
    // reset custom interval
    onUpdateContext?.({ interval: plan.interval_months });
  }, [plan]);

  // Clear Custom Renewal Date when switching away from Sales-led Invoice
  useEffect(() => {
    if (!isSalesLed || isStripeBilling) {
      if (context.custom_renewal_date) {
        onUpdateContext?.({ custom_renewal_date: undefined });
      }
    }
  }, [isSalesLed, isStripeBilling]);

  // --- product helpers --------------------------------------------------------

  /**
   * Check whether the product of the same type has been selected already.
   * @param product
   */
  const checkProductSameProductPicked = (product: Product): boolean => {
    const productTypes = selectedProducts.map((p) => p.type);
    return productTypes.includes(product.type);
  };

  /**
   * Check whether the product should be disabled in the list.
   * The product should be disabled if a product of the same type is already selected.
   * @param product
   */
  const checkProductDisabled = (product: Product): boolean => {
    const selectedProductIds = selectedProducts.map((p) => p.id);
    if (selectedProductIds.includes(product.id)) return false;
    return checkProductSameProductPicked(product);
  };

  /**
   * Check whether the product should be marked as checked.
   * @param product
   */
  const checkProductChecked = (product: Product): boolean => {
    const selectedProductIds = selectedProducts.map((p) => p.id);
    return selectedProductIds.includes(product.id);
  };

  /**
   * Toggle product selection and update the custom volumes state.
   * When the product is newly selected we add to the list.
   * When the product is deselected we remove it from the list and reset its volumes.
   * @param product
   */
  const handleToggleProduct = (product: Product) => {
    if (checkProductChecked(product)) {
      onUpdateProducts?.(selectedProducts.filter((p) => p.id !== product.id));
      onUpdateContext?.({ volumes: omit(context.volumes ?? {}, product.id) });
    }

    if (!checkProductSameProductPicked(product)) {
      onUpdateProducts?.([...selectedProducts, product]);
    }
  };

  const addInitialVolume = (product: Product) => {
    onUpdateContext?.({
      volumes: { ...context.volumes, [product.id]: getProductVolume(product, context) ?? 1 },
    });
  };

  return (
    <Grid container spacing={3}>
      {/* ------------------------------ Main Section --------------------------- */}
      <Grid item xs={12}>
        <Typography variant="subtitle2">Main Section</Typography>
        <Divider sx={{ my: 1 }} />
      </Grid>

      {/* Subscription Name */}
      <Grid item md={6} sm={12} xs={12}>
        <TextField
          variant="outlined"
          fullWidth
          label="Subscription Name"
          value={context.name ?? ''}
          onChange={handleContextChange('name', 'string')}
        />
        <FormHelperText>Falls back to a plan name if not specified</FormHelperText>
      </Grid>

      {/* Acquisition Channel (Create: editable; Update: read-only) */}
      <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
        <FormControl variant="outlined" fullWidth required>
          <InputLabel>Acquisition Channel</InputLabel>
          <Select
            required
            fullWidth
            variant="outlined"
            value={acquisition}
            onChange={({ target }) => {
              const { value } = target;
              setManualChannelOverride((prev) => ({ ...prev, acquisition: true }));
              onUpdateContext?.({
                ['acquisition_channel']: (value as AcquisitionChannel) ?? undefined,
              });
            }}
            disabled={isEditing}
          >
            <MenuItem value={AcquisitionChannel.SALES}>Sales led</MenuItem>
            <MenuItem value={AcquisitionChannel.SELF_SERVE}>Self serve</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* Billing Channel (Create: editable; Update Sales-led: editable; Update Self-serve: read-only) */}
      <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
        <FormControl variant="outlined" fullWidth required>
          <InputLabel>Billing Channel</InputLabel>
          <Select
            required
            fullWidth
            variant="outlined"
            value={billing}
            onChange={({ target }) => {
              const { value } = target;
              setManualChannelOverride((prev) => ({ ...prev, billing: true }));
              onUpdateContext?.({
                ['billing_channel']: (value as BillingChannel) ?? undefined,
              });
            }}
            disabled={isUpdateSelfServe}
          >
            <MenuItem value={BillingChannel.INVOICE}>{BillingChannel.INVOICE}</MenuItem>
            <MenuItem value={BillingChannel.STRIPE}>{BillingChannel.STRIPE}</MenuItem>
            <MenuItem value={BillingChannel.AWS_MARKETPLACE}>{BillingChannel.AWS_MARKETPLACE}</MenuItem>
            <MenuItem value={BillingChannel.NONE}>{BillingChannel.NONE}</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {/* MRR fields placement depends on mode */}
      {(!isEditing || isUpdateSalesLed) && (
        <>
          <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
            <TextField
              variant="outlined"
              fullWidth
              label="MRR (Public)"
              value={context.mrr ?? ''}
              onChange={handleContextChange('mrr', 'number')}
            />
            <FormHelperText>Custom subscription amount displayed to customers</FormHelperText>
          </Grid>

          <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
            <TextField
              variant="outlined"
              fullWidth
              label="MRR (Private)"
              value={context.mrr_private ?? ''}
              onChange={handleContextChange('mrr_private', 'number')}
            />
            <FormHelperText>Custom subscription amount used internally</FormHelperText>
          </Grid>
        </>
      )}

      {/* Custom Renewal Date moved to Main Section (Sales-led only) */}
      {isSalesLed && (
        <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
          <TextField
            type="date"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            fullWidth
            label="Custom Renewal Date"
            value={
              context.custom_renewal_date ? moment.unix(context.custom_renewal_date).format(moment.HTML5_FMT.DATE) : ''
            }
            onChange={handleContextChange('custom_renewal_date', 'timestamp')}
          />
        </Grid>
      )}

      {/* ------------------------------ SaaS Debug Section --------------------------- */}
      <Grid item xs={12}>
        <Box mt={1} />
        <Typography variant="subtitle2">SaaS Debug Section</Typography>
        <Divider sx={{ my: 1 }} />
      </Grid>

      {/* Payments Interval */}
      <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
        <FormControl variant="outlined" fullWidth>
          <InputLabel id="payments-interval-label">Payments Interval</InputLabel>
          <Select
            labelId="payments-interval-label"
            label="Payments Interval"
            value={context.interval ?? plan.interval_months}
            onChange={(e) => onUpdateContext?.({ interval: Number(e.target.value) })}
          >
            <MenuItem value={1}>1 month</MenuItem>
            <MenuItem value={2}>2 months</MenuItem>
            <MenuItem value={3}>3 months</MenuItem>
            <MenuItem value={6}>6 months</MenuItem>
            <MenuItem value={9}>9 months</MenuItem>
            <MenuItem value={12}>1 year</MenuItem>
          </Select>
          <FormHelperText>Defaults to the selected plan duration</FormHelperText>
        </FormControl>
      </Grid>

      {/* MRR fields for Update (Self-serve) only */}
      {isUpdateSelfServe && (
        <>
          <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
            <TextField
              variant="outlined"
              fullWidth
              label="MRR (Public)"
              value={context.mrr ?? ''}
              onChange={handleContextChange('mrr', 'number')}
            />
            <FormHelperText>Custom subscription amount displayed to customers</FormHelperText>
          </Grid>
          <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
            <TextField
              variant="outlined"
              fullWidth
              label="MRR (Private)"
              value={context.mrr_private ?? ''}
              onChange={handleContextChange('mrr_private', 'number')}
            />
            <FormHelperText>Custom subscription amount used internally</FormHelperText>
          </Grid>
        </>
      )}

      <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
        <FormControl variant="outlined" fullWidth required>
          <InputLabel id="discount-label">Discount</InputLabel>
          <Select
            labelId="discount-label"
            label="Discount"
            required
            fullWidth
            variant="outlined"
            value={selectedDiscount?.id || ''}
            onChange={({ target }) => onUpdateDiscount?.(displayDiscounts.find((d) => d.id === target.value))}
            MenuProps={{ disablePortal: false }}
            disabled={!isSelfServe}
          >
            <MenuItem key="none" value="">
              None
            </MenuItem>
            {displayDiscounts.map((disc) => (
              <MenuItem key={disc.id} value={disc.id}>
                {disc.name} [{disc.duration_months === -1 ? 'forever' : `${disc.duration_months} months`}{' '}
                {disc.discount_percentage ? `${disc.discount_percentage}%` : disc.discount_flat}]
              </MenuItem>
            ))}
          </Select>

          <FormHelperText>
            {isSelfServe
              ? 'Editable for when Acquisition is Self serve'
              : isEditing
                ? ''
                : 'Disabled. Always set to 100% Custom Payment [Forever] for new Sales led subscriptions.'}
          </FormHelperText>
        </FormControl>
      </Grid>

      {/* Backdating (3 fields) */}
      <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
        {edit ? (
          <FormControl variant="outlined" fullWidth required>
            <InputLabel>Billing Cycle Anchor</InputLabel>
            <Select
              required
              fullWidth
              variant="outlined"
              value={(context.billing_cycle_anchor ?? 'unchanged').toString()}
              onChange={({ target }) =>
                onUpdateContext?.({
                  billing_cycle_anchor: target.value === 'unchanged' ? undefined : target.value,
                })
              }
            >
              {['unchanged', 'now'].map((e) => (
                <MenuItem key={e} value={e}>
                  {e}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <TextField
            type="date"
            variant="outlined"
            InputLabelProps={{ shrink: true }}
            fullWidth
            label="Billing Cycle Anchor"
            value={
              context.billing_cycle_anchor
                ? moment.unix(context.billing_cycle_anchor as number).format(moment.HTML5_FMT.DATE)
                : ''
            }
            onChange={handleContextChange('billing_cycle_anchor', 'timestamp')}
          />
        )}
        <FormHelperText>When the paid period of subscription should start, defaults to creation date</FormHelperText>
      </Grid>

      <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
        <TextField
          variant="outlined"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="date"
          label="Backdate Start Date"
          value={
            context.backdate_start_date ? moment.unix(context.backdate_start_date).format(moment.HTML5_FMT.DATE) : ''
          }
          onChange={handleContextChange('backdate_start_date', 'timestamp')}
        />
        <FormHelperText>Backdate the subscription start date, defaults to current date</FormHelperText>
      </Grid>

      <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
        <FormControl variant="outlined" fullWidth required>
          <InputLabel>Proration Behavior</InputLabel>
          <Select
            required
            fullWidth
            variant="outlined"
            value={context.proration_behavior ?? 'none'}
            onChange={handleContextChange('proration_behavior', 'string')}
          >
            {['none', 'always_invoice', 'create_prorations'].map((e) => (
              <MenuItem key={e} value={e}>
                {e}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>--none-- corresponds to default behaviour configured in Stripe</FormHelperText>
        </FormControl>
      </Grid>

      {/* Hints */}
      <HintsAlert hints={infoHints} severity="info" title="Hints" />
      <HintsAlert hints={warnHints} severity="warning" title="Heads up" />

      {/* Products ------------------------------------------------------------- */}
      <Grid item xl={12} lg={12} md={12} sm={12} xs={12}>
        <Grid container spacing={3}>
          <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
            <Typography variant="subtitle2" paragraph>
              Products to Include
            </Typography>
            <Divider sx={{ my: 1 }} />
            <List>
              {products?.map((product) => (
                <ListItem
                  button
                  key={product.id}
                  disabled={checkProductDisabled(product)}
                  onClick={() => handleToggleProduct(product)}
                >
                  <ListItemAvatar>
                    <PlanProductIcon productType={product.type} />
                  </ListItemAvatar>
                  <ListItemText primary={product.name} secondary={product.id} />
                  <ListItemSecondaryAction>
                    <Checkbox
                      color="primary"
                      onChange={() => handleToggleProduct(product)}
                      checked={checkProductChecked(product)}
                      disabled={checkProductDisabled(product)}
                    />
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Grid>

          <Grid item xl={6} lg={6} md={6} sm={12} xs={12}>
            <Typography variant="subtitle2" paragraph>
              Selected Products
            </Typography>
            <Divider sx={{ my: 1 }} />
            <List>
              {selectedProducts.map((product) => (
                <ListItem key={product.id}>
                  <ListItemAvatar>
                    <PlanProductIcon productType={product.type} />
                  </ListItemAvatar>
                  <ListItemText
                    secondaryTypographyProps={{ component: 'div' }}
                    primary={product.name}
                    secondary={
                      <>
                        {!PRICELESS_PLAN_FAMILIES.includes(plan.family as PlanFamily) &&
                          formatMonetaryAmount(getProductEstimations(product, context), plan.currency)}{' '}
                        | Volume: {product.type === ProductType.SEATS && <>{context.seats} License(s)</>}
                        {product.type === ProductType.CI_USAGE && (
                          <>{getProductVolume(product, context) ?? '∞'} Credits/Mo.</>
                        )}
                        {product.type === ProductType.POD_USAGE && (
                          <>{formatBytes(getProductVolume(product, context)) ?? '∞'}</>
                        )}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    {!!context.volumes?.[product.id] && (
                      <TextField
                        type="number"
                        variant="outlined"
                        fullWidth
                        value={context.volumes[product.id] ?? ''}
                        onChange={(e) =>
                          onUpdateContext?.({
                            volumes: {
                              ...context.volumes,
                              [product.id]: Number.parseInt(e.target.value, 10),
                            },
                          })
                        }
                      />
                    )}
                    {!context.volumes?.[product.id] && product.type !== ProductType.SEATS && (
                      <Button color="primary" size="small" variant="outlined" onClick={() => addInitialVolume(product)}>
                        Custom Vol.
                      </Button>
                    )}
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
